import React, { Fragment, ReactNode, useEffect, useState } from 'react';

import { ConfirmationMessages, RadioButtonItem } from '@netfront/ui-library';
import cx from 'classnames';

import { getHtmlElementOnChangeValueFromEvent } from '../Snippets.helpers';

import { RadioButtonSnippetProps } from './RadioButtonSnippet.interfaces';

import './RadioButtonSnippet.css';

const RadioButtonSnippet = ({
  id,
  isLabelHidden,
  isRequired,
  label,
  message,
  name,
  onChange,
  options,
  selectedValue,
}: RadioButtonSnippetProps) => {
  const [radioButtonOptionLabelComponentMap, setRadioButtonOptionLabelComponentMap] = useState<Record<string, ReactNode>>();

  useEffect(() => {
    const loadRadioButtonOptionLabelComponentMap = async () => {
      const radioButtonOptionLabelComponents = await Promise.all(
        options.map(async ({ asset, id: optionId }) => {
          const { alt, presignedUrl } = asset ?? {};

          const labelContentComponent = (
            <Fragment>
              {presignedUrl && <img alt={alt} className="c-radio-button-asset" src={presignedUrl} />}
              <span aria-hidden="true" className="c-radio-button__icon" />
            </Fragment>
          );

          return [
            {
              [optionId]: labelContentComponent,
            },
          ];
        })
      );

      setRadioButtonOptionLabelComponentMap(
        radioButtonOptionLabelComponents.flat().reduce(
          (accumulator, item) => ({
            ...accumulator,
            ...item,
          }),
          {}
        )
      );
    };

    loadRadioButtonOptionLabelComponentMap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { error: errorMessage, success: successMessage } = message ?? {};

  return (
    <fieldset aria-describedby={errorMessage ? `id_${name}_error` : undefined}>
      <legend className="c-label">
        <span
          className={cx('c-label__text', {
            'h-hide-visually': isLabelHidden,
          })}
        >
          {label}
        </span>
        {isRequired && (
          <abbr aria-label="required" className="is-required" title="required">
            *
          </abbr>
        )}
      </legend>
      {options.map(({ deleteButton, id: optionId, isDisabled, label: optionLabel, value: optionValue }) => {
        const labelContentComponent = radioButtonOptionLabelComponentMap ? radioButtonOptionLabelComponentMap[optionId] : null;
        const radioButtonId = `${id}-${optionId}`;

        return (
          <div key={`${radioButtonId}-container`} className="c-radio-button__container">
            <RadioButtonItem
              id={radioButtonId}
              isChecked={optionValue === selectedValue}
              isDisabled={isDisabled}
              labelContentComponent={labelContentComponent}
              labelText={optionLabel}
              name={String(name)}
              value={optionValue}
              onChange={(event) => {
                const updatedSelectedValue = getHtmlElementOnChangeValueFromEvent(event);

                onChange(updatedSelectedValue);
              }}
            />
            {deleteButton}
          </div>
        );
      })}
      <ConfirmationMessages errorMessage={errorMessage} id={id} successMessage={successMessage} />
    </fieldset>
  );
};

export { RadioButtonSnippet };
